import { useCallback, useRef } from 'react';

interface UseInfiniteScrollOptions {
  enabled: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}

export const useInfiniteScroll = <T extends HTMLElement>({
  enabled,
  hasMore,
  onLoadMore,
}: UseInfiniteScrollOptions): ((node: T | null) => void) => {
  const observer = useRef<IntersectionObserver | null>(null);
  const canLoadMore = enabled && hasMore;

  return useCallback((node: T | null) => {
    if (!enabled) {
      return;
    }

    if (observer.current) {
      observer.current.disconnect();
    }

    observer.current = new IntersectionObserver((entries) => {
      const firstEntry = entries[0];
      if (firstEntry?.isIntersecting && canLoadMore) {
        onLoadMore();
      }
    });

    if (node) {
      observer.current.observe(node);
    }
  }, [canLoadMore, enabled, onLoadMore]);
};

export default useInfiniteScroll;
